import {
	Alert,
	AlertDescription,
	AlertIcon,
	Box,
	Button,
	Flex,
	Heading,
	Input,
	SimpleGrid,
	Stack,
	Text,
	useToast,
} from "@chakra-ui/react";
import { ExternalLink, Link as LinkIcon, Linkedin, MessageCircle } from "lucide-react";
import React from "react";
import {
	buildDirectLaborPassLinkCopy,
	buildLinkedInLaborPassCopy,
	type LaborPassCompletionPortfolio,
	buildWhatsAppLaborPassShareTarget,
	getLaborPassRemainingCompletionPrompt,
	LaborPassCompletionState,
} from "./laborpass-helpers";

interface LaborPassCompletionCardProps {
	completionState: Exclude<LaborPassCompletionState, "incomplete">;
	portfolio: LaborPassCompletionPortfolio;
	publicUrl: string;
}

interface ShareActionRowProps {
	action: React.ReactNode;
	description: string;
	icon: React.ReactNode;
	title: string;
}

const ShareActionRow = ({
	action,
	description,
	icon,
	title,
}: ShareActionRowProps) => (
	<Flex
		border="1px solid #E7E0DA"
		bg="white"
		p={4}
		direction="column"
		align="start"
		gap={4}
	>
		<Flex align="start" gap={3}>
			<Box
				border="1px solid #E7E0DA"
				bg="#FFF7F2"
				p={2}
				display="flex"
				alignItems="center"
				justifyContent="center"
			>
				{icon}
			</Box>
			<Stack spacing={1}>
				<Text fontWeight={600} color="#121212">
					{title}
				</Text>
				<Text color="#4B4B4B" fontSize="sm">
					{description}
				</Text>
			</Stack>
		</Flex>
		<Box w="full">{action}</Box>
	</Flex>
);

const PublicLaborPassPreview = ({
	onLoadError,
	publicUrl,
}: {
	onLoadError: () => void;
	publicUrl: string;
}) => (
	<Box
		border="1px solid #E7E0DA"
		bg="white"
		overflow="hidden"
		h="full"
		minH="480px"
		flex="1"
	>
		<iframe
			src={publicUrl}
			title="Public LaborPass preview"
			className="h-full min-h-[480px] w-full bg-white"
			onError={onLoadError}
		/>
	</Box>
);

export const LaborPassCompletionCard = ({
	completionState,
	portfolio,
	publicUrl,
}: LaborPassCompletionCardProps) => {
	const toast = useToast();
	const [previewFailed, setPreviewFailed] = React.useState(false);
	const [manualCopyValue, setManualCopyValue] = React.useState<string | null>(
		null
	);

	const linkedInCopy = buildLinkedInLaborPassCopy(publicUrl);
	const directLinkCopy = buildDirectLaborPassLinkCopy(publicUrl);
	const remainingCompletionPrompt = getLaborPassRemainingCompletionPrompt(portfolio);

	const copyToClipboard = async (
		value: string,
		successMessage: { description?: string; title: string }
	) => {
		try {
			if (!navigator.clipboard?.writeText) {
				throw new Error("Clipboard unavailable");
			}

			await navigator.clipboard.writeText(value);
			setManualCopyValue(null);
			toast({
				title: successMessage.title,
				description: successMessage.description,
				status: "success",
				duration: 2500,
				isClosable: true,
			});
		} catch (_error) {
			setManualCopyValue(value);
			toast({
				title: "Copy not available in this browser",
				description: "Use the link shown below to copy your LaborPass manually.",
				status: "warning",
				duration: 4000,
				isClosable: true,
			});
		}
	};

	const handleWhatsAppShare = () => {
		const shareTarget = buildWhatsAppLaborPassShareTarget(publicUrl);
		window.open(shareTarget, "_blank", "noopener,noreferrer");
	};

	const heading =
		completionState === "fully-complete"
			? "Your LaborPass is fully built and ready to share"
			: "Your LaborPass core setup is complete";

	const supportingCopy =
		completionState === "fully-complete"
			? "Your profile overview, work history, certifications, and education are all in place. Share your LaborPass anywhere you introduce yourself professionally."
			: "Your profile overview and work history are complete, so clients can already review your LaborPass publicly.";

	return (
		<Box border="1px solid #E7E0DA" bg="#FFF9F5" p={{ base: 5, lg: 8 }}>
			<SimpleGrid columns={{ base: 1, xl: 2 }} gap={8} alignItems="stretch">
				<Stack spacing={5}>
					<Stack spacing={3}>
						<Text
							fontSize="sm"
							fontWeight={600}
							letterSpacing="0.08em"
							textTransform="uppercase"
							color="#A5533B"
						>
							LaborPass milestone
						</Text>
						<Heading as="h2" fontSize={{ base: "28px", lg: "36px" }}>
							{heading}
						</Heading>
						<Text color="#464646" fontSize={{ base: "md", lg: "lg" }}>
							{supportingCopy}
						</Text>
						{completionState === "required-complete" && remainingCompletionPrompt && (
							<Box borderLeft="4px solid #E0623A" pl={4}>
								<Text color="#464646">{remainingCompletionPrompt}</Text>
							</Box>
						)}
					</Stack>

					<Button
						alignSelf="start"
						colorScheme="earthRed"
						variant="outline"
						rightIcon={<ExternalLink className="h-4 w-4" />}
						onClick={() => window.open(publicUrl, "_blank", "noopener,noreferrer")}
					>
						Open public view
					</Button>

					<Stack spacing={3}>
						<ShareActionRow
							title="WhatsApp"
							description="Share with clients when bidding for jobs - let them see your credentials before they decide."
							icon={<MessageCircle className="h-5 w-5 text-[#A5533B]" />}
							action={
								<Button
									colorScheme="brand"
									variant="solid"
									w="full"
									onClick={handleWhatsAppShare}
								>
									Share on WhatsApp
								</Button>
							}
						/>
						<ShareActionRow
							title="LinkedIn"
							description="Add it to your profile to build professional credibility and get discovered by more clients."
							icon={<Linkedin className="h-5 w-5 text-[#A5533B]" />}
								action={
								<Button
									colorScheme="earthRed"
									variant="outline"
									w="full"
									onClick={() =>
										void copyToClipboard(
											linkedInCopy,
											{
												title: "Copied to clipboard",
												description:
													"You can add this LaborPass link to your LinkedIn profile.",
											}
										)
									}
								>
									Copy for LinkedIn
								</Button>
							}
						/>
						<ShareActionRow
							title="Direct link"
							description="Paste into proposals, CVs, or anywhere you introduce yourself professionally."
							icon={<LinkIcon className="h-5 w-5 text-[#A5533B]" />}
							action={
								<Button
									colorScheme="earthRed"
									variant="outline"
									w="full"
									onClick={() =>
										void copyToClipboard(directLinkCopy, {
											title: "Copied to clipboard",
										})
									}
								>
									Copy link
								</Button>
							}
						/>
					</Stack>

					{manualCopyValue && (
						<Alert status="warning" bg="white" border="1px solid #E7E0DA">
							<AlertIcon />
							<Stack spacing={3} flex="1">
								<AlertDescription color="#4B4B4B">
									Copy the link from here if your browser blocks clipboard access.
								</AlertDescription>
								<Input value={manualCopyValue} readOnly bg="white" />
							</Stack>
						</Alert>
					)}
				</Stack>

				<Stack spacing={3} h="full">
					<Text fontWeight={600} color="#121212">
						Preview your public LaborPass
					</Text>
					{previewFailed ? (
						<Flex
							border="1px solid #E7E0DA"
							bg="white"
							minH="480px"
							flex="1"
							p={8}
							direction="column"
							justify="center"
							align="start"
							gap={4}
						>
							<Heading as="h3" fontSize="xl">
								Preview unavailable right now
							</Heading>
							<Text color="#464646">
								Open your public LaborPass in a new tab to review it directly.
							</Text>
							<Button
								colorScheme="earthRed"
								variant="outline"
								rightIcon={<ExternalLink className="h-4 w-4" />}
								onClick={() =>
									window.open(publicUrl, "_blank", "noopener,noreferrer")
								}
							>
								Open public view
							</Button>
						</Flex>
					) : (
						<PublicLaborPassPreview
							publicUrl={publicUrl}
							onLoadError={() => setPreviewFailed(true)}
						/>
					)}
				</Stack>
			</SimpleGrid>
		</Box>
	);
};
